import * as CL from '@design-system/component-library';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { generatePath, useLocation, useNavigate, useRevalidator } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses';
import { paths } from '../../common/constants/pathVariables';
import { AddOnVisibilityOperationType, SubscriptionAddonCode } from '../../common/enums';
import { changeSubscriptionFeatures, changeSubscriptionMobilePbx } from '../../common/fetch';
import { useAddOnVisibility } from '../../common/hooks/useAddOnVisibility';
import { useSearchParams } from '../../common/hooks/useSearchParams.js';
import {
  cancelMsg,
  confirmMsg,
  somethingWentWrongMsg,
  subscriptionMsg,
  t,
  vakioAdditionalSettingsMsg,
} from '../../common/i18n/index.js';
import { lookupAddonPrice } from '../../common/utils/addOnUtils';
import { forceInternationalPhoneNumberFormat, formatPhoneNumber } from '../../common/utils/phoneNumberUtils.js';
import { formatSumToString } from '../../common/utils/priceUtils';
import { getSubscriptionUrlId, isReachabilityWithMobile } from '../../common/utils/subscriptionUtils';
import { NumberRange } from '../../generated/api/numberRange.js';
import { SubscriptionPbxConfiguration } from '../../generated/api/subscriptionPbxConfiguration.js';
import { SubscriptionPbxDetails } from '../../generated/api/subscriptionPbxDetails.js';
import { SubscriptionType } from '../../generated/api/subscriptionType.js';
import { addNotification } from '../../public/site/notificationStore.js';
import { AttachPbxContentConfirmationDialogContent } from '../AttachPbxContentConfirmationDialogContent/AttachPbxContentConfirmationDialogContent.js';
import { ConfirmationModal } from '../Modal/ConfirmationModal';
import { getReachabilityWithMobilePayload } from '../PbxAccordionContent/PbxAccordionUtils';
import { SubscriptionAdditionalService } from '../SubscriptionAdditionalService/SubscriptionAdditionalService';
import { reachabilityDefaultValues } from '../SubscriptionDetails/addOnDependencyUtils';
import {
  getCurrentPbxSolutionNumberRanges,
  getExtensionNumber,
  getExtensionRangeId,
  getSelectedPbxSolution,
  isPbxConfigComplete,
  sortNumberRangesInPbxSolutions,
} from './attachPbxUtils';
import { CorporateNumberRangeSelect } from './components/CorporateNumberRangeSelect.js';
import { PbxSolutionSelect } from './components/PbxSolutionSelect.js';
import { VakioCorporateNumberSelect } from './components/VakioCorporateNumberSelect';
import type { NumberRangeData } from '../../common/loaders';
import type { CategoryKey } from '../../common/utils/categoryUtils.js';
import type { CompanyInfoResponse } from '../../generated/api/companyInfoResponse.js';
import type { Subscription } from '../../generated/api/subscription.js';
import type { AssociationRecord } from '../../legacy/cloudsense-add-on-dependency-engine/AddOnManagerOnline';

import './AttachPbxContent.scss';

export interface AttachVakioPbxContentProps {
  category: CategoryKey;
  companyInfo?: CompanyInfoResponse;
  numberRanges: NumberRangeData[];
  subscription: Subscription;
  onClickSubscription: (id: string) => void;
  addOnAssociations: AssociationRecord[];
}

export interface AttachVakioPbxContentFormValues {
  pbxSolutionId: string;
  corporateNumberRangeId?: string;
  corporateNumber?: string;
  extensionNumber?: string;
  extensionRangeId?: string;
  currentNumberRange?: NumberRangeData;
}

const createCompletePbxConfiguration = (
  configuration: AttachVakioPbxContentFormValues,
  subscription?: Subscription
): SubscriptionPbxConfiguration => ({
  ...configuration,
  busyAction: SubscriptionPbxConfiguration.BusyActionEnum.NEXT_NUMBER_IN_CHAIN,
  corporateNumber: forceInternationalPhoneNumberFormat(configuration.corporateNumber)!,
  delayForChainCalls: 20,
  pbxConfigurationDetails: { currentActiveChain: SubscriptionPbxDetails.CurrentActiveChainEnum.WORKING_HOURS },
  subscriptionDisplayId: subscription?.subscriptionDisplayId,
  pbxSolutionId: configuration.pbxSolutionId,
  workingHoursReachabilityChain: subscription?.details?.mobile?.mobileNumber
    ? [subscription.details.mobile.mobileNumber]
    : undefined,
});

const getPbxSolutions = (pbxSolutions: Subscription[]) => {
  const filteredSolutions = pbxSolutions.filter(
    pbxSolution => pbxSolution.subscriptionType === SubscriptionType.MOBILE_PBX_LITE
  );
  return filteredSolutions.length > 0 ? filteredSolutions : pbxSolutions;
};

export const AttachVakioPbxContent = ({
  companyInfo,
  subscription,
  numberRanges,
  onClickSubscription,
  addOnAssociations,
}: AttachVakioPbxContentProps) => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { mdmId } = useSearchParams<{ mdmId: string }>();
  const { revalidate } = useRevalidator();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [addReachabilityAddon, setAddReachabilityAddon] = useState(false);

  const { addOnVisibilities } = useAddOnVisibility(
    subscription.commercialProductCode || '',
    AddOnVisibilityOperationType.CHANGE,
    undefined,
    mdmId
  );

  const mobileReachability = lookupAddonPrice(addOnAssociations, SubscriptionAddonCode.REACHABILITY_WITH_MOBILE);

  const pbxSolutions = getPbxSolutions(companyInfo?.pbxSolutions ?? []);
  const defaultPbxSolutionId = sortNumberRangesInPbxSolutions(pbxSolutions)[0].subscriptionId;
  const defaultPbxSolution = getSelectedPbxSolution(pbxSolutions, defaultPbxSolutionId);

  const corporateNumberRanges = defaultPbxSolution.details!.mobilePbx!.numberRanges.filter(
    numberRange => numberRange.rangeType === NumberRange.RangeTypeEnum.PSTN
  );
  const defaultCorporateRange = corporateNumberRanges[0].rangeId;
  const defaultCorporateNumbers = numberRanges.find(item => item.rangeId === defaultCorporateRange);
  const defaultCorporateNumber = defaultCorporateNumbers?.displayedNumbers
    ? defaultCorporateNumbers.displayedNumbers[0]
    : undefined;

  const extensionRangeId = defaultCorporateNumber
    ? getExtensionRangeId(defaultPbxSolution, defaultCorporateNumber)
    : undefined;
  const defaultExtensionNumber =
    extensionRangeId && defaultCorporateNumber && numberRanges
      ? getExtensionNumber(extensionRangeId, defaultCorporateNumber, numberRanges)
      : undefined;

  const methods = useForm<AttachVakioPbxContentFormValues>({
    defaultValues: {
      corporateNumber: defaultCorporateNumber,
      corporateNumberRangeId: defaultCorporateRange,
      pbxSolutionId: defaultPbxSolutionId,
      currentNumberRange: numberRanges.find(i => i.rangeId === defaultCorporateRange),
      extensionRangeId: extensionRangeId,
      extensionNumber: defaultExtensionNumber,
    },
  });

  const formValues = methods.watch();
  const pbxConfiguration = createCompletePbxConfiguration(formValues, subscription);
  const selectedPbxSolution = getSelectedPbxSolution(pbxSolutions, formValues.pbxSolutionId);

  const onConfirm = async () => {
    if (isPbxConfigComplete(pbxConfiguration)) {
      setIsSaving(true);
      const payload = {
        ...pbxConfiguration,
        pbxConfigurationDetails: {
          ...pbxConfiguration.pbxConfigurationDetails,
          offWorkReachabilityChain: pbxConfiguration.pbxConfigurationDetails.offWorkReachabilityChain
            ? pbxConfiguration.pbxConfigurationDetails.offWorkReachabilityChain.filter(
                phoneNumber => phoneNumber.length > 0
              )
            : undefined,
        },
        workingHoursReachabilityChain: pbxConfiguration.workingHoursReachabilityChain
          ? pbxConfiguration.workingHoursReachabilityChain.filter(phoneNumber => phoneNumber.length > 0)
          : undefined,
      };

      const reachabilityAddOn = addOnVisibilities?.find(visibility =>
        isReachabilityWithMobile(visibility.addOn?.addOnCode)
      );

      try {
        await changeSubscriptionMobilePbx(payload, subscription.subscriptionId, mdmId);
        if (addReachabilityAddon && reachabilityAddOn?.addOn) {
          await changeSubscriptionFeatures(
            getReachabilityWithMobilePayload(
              reachabilityAddOn?.addOn ? [reachabilityAddOn.addOn] : [],
              [],
              [],
              subscription,
              {
                [SubscriptionAddonCode.REACHABILITY_WITH_MOBILE]: reachabilityDefaultValues,
              }
            ),
            subscription.subscriptionId,
            mdmId
          );
        }

        addNotification(t.T29V('Change order successful'), 'success');
        navigate(
          generatePath(paths.PS_MOBILE_SUBSCRIPTION, { subscriptionId: getSubscriptionUrlId(subscription) }) + search
        );
        // Refresh /pending-subscription-actions data to update subscription status
        revalidate();
      } catch (error) {
        addNotification(t.JDTO(somethingWentWrongMsg), 'error');
        setIsSaving(false);
      }
    }
  };

  const onPbxSolutionSelect = (value: string) => {
    methods.setValue('pbxSolutionId', value);
    const newPbxSolution = getSelectedPbxSolution(pbxSolutions, value);
    const newCorporateNumberRanges = newPbxSolution.details!.mobilePbx!.numberRanges.filter(
      numberRange => numberRange.rangeType === NumberRange.RangeTypeEnum.PSTN
    );
    const newCorporateRange = newCorporateNumberRanges[0].rangeId;
    const newNumberRange = numberRanges.find(i => i.rangeId === newCorporateRange);
    const newCorporateNumber = newNumberRange?.displayedNumbers ? newNumberRange?.displayedNumbers[0] : undefined;
    const newExtensionRangeId = getExtensionRangeId(newPbxSolution, newCorporateNumber);
    const newExtensionNumber =
      newExtensionRangeId && newCorporateNumber && numberRanges
        ? getExtensionNumber(newExtensionRangeId, newCorporateNumber, numberRanges)
        : undefined;

    methods.setValue('corporateNumberRangeId', newCorporateRange);
    methods.setValue('corporateNumber', newCorporateNumber);
    methods.setValue('extensionNumber', newExtensionNumber);
    methods.setValue('extensionRangeId', newExtensionRangeId);
    methods.setValue(
      'currentNumberRange',
      numberRanges.find(i => i.rangeId === newCorporateRange)
    );
  };

  const onNumberRangeSelect = (value: string) => {
    const newNumberRange = numberRanges.find(i => i.rangeId === value);
    const newCorporateNumber = newNumberRange?.displayedNumbers ? newNumberRange?.displayedNumbers[0] : undefined;
    const newExtensionRangeId = getExtensionRangeId(selectedPbxSolution, newCorporateNumber);
    const newExtensionNumber =
      newExtensionRangeId && newCorporateNumber && numberRanges
        ? getExtensionNumber(newExtensionRangeId, newCorporateNumber, numberRanges)
        : undefined;
    methods.setValue('corporateNumberRangeId', value);
    methods.setValue('currentNumberRange', newNumberRange);
    methods.setValue('corporateNumber', newCorporateNumber);
    methods.setValue('extensionRangeId', newExtensionRangeId);
    methods.setValue('extensionNumber', newExtensionNumber);
  };

  return (
    <div className="of-attach-pbx-content">
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(() => setShowConfirmationModal(true))}>
          <h3>{t.IM77('Vakio settings of subscription')}</h3>
          <p>
            {t.TETZ(
              `Elisa Puheratkaisu Vakio is an easy way of making mobile phones part of a company's telephone system.`
            )}
          </p>
          <CL.Description
            items={[
              {
                title: t.P674(subscriptionMsg),
                description: formatPhoneNumber(subscription?.details?.mobile?.mobileNumber || ''),
              },
            ]}
          />
          <PbxSolutionSelect
            pbxSolutions={pbxSolutions}
            isRing={false}
            onSelect={onPbxSolutionSelect}
            selectedValue={formValues.pbxSolutionId}
          />

          <CorporateNumberRangeSelect
            selectedValue={formValues.currentNumberRange?.rangeId}
            onSelect={onNumberRangeSelect}
            currentPbxSolutionNumberRanges={getCurrentPbxSolutionNumberRanges(
              pbxSolutions.find(pbxSolution => pbxSolution.subscriptionId === formValues.pbxSolutionId)
            )}
          />

          <VakioCorporateNumberSelect
            numberRanges={numberRanges}
            pbxSolutions={pbxSolutions}
            addOnAssociations={addOnAssociations}
          />
          <div>
            <h4>{t.WJIG(vakioAdditionalSettingsMsg)}</h4>
            <div className="of-attach-pbx-content__additional-services">
              <div className="of-attach-pbx-content__additional-services--container">
                <div className="of-attach-pbx-content__additional-services--checkbox">
                  <CL.Checkbox
                    aria-label={t.VSWS('Reachability with mobile number')}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setAddReachabilityAddon(event.target.checked)
                    }
                  />
                </div>
                <div className="of-attach-pbx-content__additional-services--content">
                  <SubscriptionAdditionalService
                    title={t.VSWS('Reachability with mobile number')}
                    priceInfo={{
                      value: `${formatSumToString(mobileReachability!.monthlyRecurringCharge)}/${t.XXVX('month')}`,
                    }}
                  >
                    {t.WXYJ(
                      'You can add backup respondents for the mobile phone number. Vakio’s call features are now available for calls to mobile phone numbers as well. '
                    )}
                    <CL.Link
                      className={dsClass.FONT_SIZE_MEDIUM}
                      linkTarget="_blank"
                      linkHref="https://yrityksille.elisa.fi/ohjeet/vakio-vinkit"
                    >
                      {t.HKO6('Read more about reachability with mobile phone number')}
                      <CL.Icon icon="external-link" />
                    </CL.Link>
                  </SubscriptionAdditionalService>
                </div>
              </div>
            </div>
          </div>

          <div className="of-barrings-content__actions">
            <>
              <CL.Button disabled={!isPbxConfigComplete(formValues)} size="l" color="primary" type="submit">
                {t.QVYK(confirmMsg)}
              </CL.Button>
              <CL.Button size="l" color="link" onClick={() => onClickSubscription(subscription!.subscriptionDisplayId)}>
                {t.B2V1(cancelMsg)}
              </CL.Button>
            </>
          </div>
          {showConfirmationModal && (
            <ConfirmationModal
              heading={t.DQ1Z('Vakio solution for subscription')}
              onConfirm={onConfirm}
              onCancel={() => setShowConfirmationModal(false)}
              isLoading={isSaving}
            >
              <AttachPbxContentConfirmationDialogContent
                numberCategory={formValues.currentNumberRange?.numberCategory}
                pbxConfig={pbxConfiguration}
                voiceSub={subscription!}
                addReachabilityAddon={addReachabilityAddon}
                addOnAssociations={addOnAssociations}
              />
            </ConfirmationModal>
          )}
        </form>
      </FormProvider>
    </div>
  );
};
